import { Collapsible, CollapsibleRoot, CollapsibleTrigger, space } from '@sunwebgroup/ui-components'
import { Button, Icon } from '@sunwebgroup/ui-components/uds'
import { Children, isValidElement, type PropsWithChildren, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { splitAtNth } from './splitAtNth'

type ShowMoreProps = PropsWithChildren<{
  className?: string
  closeLabel: string
  minimumHiddenItems?: number
  openLabel: string
  visibleItems?: number
  onOpen?: (value: boolean) => void
}>

const ShowMoreButton = styled(Button)`
  margin: 0 auto;
  margin-top: ${space('primitives_space_8')};
  display: flex;
`

export function ShowMore({
  children,
  className,
  closeLabel,
  minimumHiddenItems = 2,
  openLabel,
  visibleItems = 3,
  onOpen,
}: ShowMoreProps) {
  const [open, setOpen] = useState(false)

  const lastCheckedIndex = Children.toArray(children)
    .map(child => isValidElement(child) && (child.props.checked as boolean))
    .lastIndexOf(true)
  const isHiddenChildChecked = lastCheckedIndex >= visibleItems

  const [shownChildren, hiddenChildren] = useMemo(
    () => splitAtNth(Children.toArray(children), visibleItems),
    [children, visibleItems],
  )

  const hiddenChildrenCount = Children.count(hiddenChildren)
  const shouldHideChildren = hiddenChildrenCount >= minimumHiddenItems

  const handleOpenChange = (value: boolean) => {
    setOpen(prev => !prev)
    onOpen?.(value)
  }

  useEffect(() => {
    if (!shouldHideChildren) {
      setOpen(false)
    }
  }, [shouldHideChildren])

  useEffect(() => {
    if (isHiddenChildChecked && !open) {
      setOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Disabled because we only want to listen to changes in 'isHiddenChildChecked' and not 'open'
  }, [isHiddenChildChecked])

  return (
    <div className={className}>
      {shownChildren}
      {!shouldHideChildren && hiddenChildren}
      {shouldHideChildren && hiddenChildren && (
        <CollapsibleRoot onOpenChange={handleOpenChange} defaultOpen={isHiddenChildChecked}>
          <Collapsible>{hiddenChildren}</Collapsible>
          <CollapsibleTrigger asChild>
            <ShowMoreButton $variant="secondary">
              {open ? closeLabel : openLabel}
              <Icon glyph={open ? 'ChevronUpExtraSmall' : 'ChevronDownExtraSmall'} />
            </ShowMoreButton>
          </CollapsibleTrigger>
        </CollapsibleRoot>
      )}
    </div>
  )
}
